/*----------------------------------
- DOMAINS FILTER RAIL
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr  ADR-0035
 * @rule The rail shows the member filters but never applies them. Accordions expand freely; a rule a click names
 *       (an option, a pattern) or a range touched opens the filter wall with the intended query in `returnTo`, a
 *       locked filter opens the plan's wall, and the rows and the URL never change.
 *
 * The rail as the mock's cards (docs/mockups/website-domains.html, `railHtml()`), rebuilt on the system in place of
 * Browse's `BrowseFilterSidebar` (website debt ledger, row 20): the filters, their order, labels and icons read from
 * the catalog the page's resolution carries, the lock from Browse's tiers. A visitor holds no advanced filter
 * (`canUseAdvancedFilters` was false on the shipped rail), so the locked set is every advanced filter.
 *
 * The page remounts the rail after every wall (`railKey`), so an open card or a pattern draft never outlives the hand-off.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useMemo, useState } from 'preact/hooks';

import { BROWSE_PATTERN_FILTER_ID, DEFAULT_BROWSE_EXPANDED_FILTER_IDS } from '@uniquedomains/components/radar/catalogs/browseFilterDefaults';
import { getRadarRuleControlIcon } from '@uniquedomains/components/radar/catalogs/radarRuleControls';
import type { RadarFilterDef, RadarQuery } from '@uniquedomains/components/radar/rules/types';

import DomainsRailAccordion from './rail/DomainsRailAccordion';
import DomainsRailChoice from './rail/DomainsRailChoice';
import DomainsRailPattern from './rail/DomainsRailPattern';
import DomainsRailRange from './rail/DomainsRailRange';
import {
    buildChoiceIntent,
    buildPatternIntent,
    readPatternOperator,
    readRangeBounds,
    readSelectedValues,
    resolveRailFilters,
    type RailFilter,
} from './rail/railModel';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsFilterRailProps = {
    filterCatalog: RadarFilterDef[];
    query: RadarQuery;
    onFilterIntent: (nextQuery: RadarQuery) => void;
    onLockedFilter: (filter: { id: string; label: string }) => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsFilterRail = ({ filterCatalog, query, onFilterIntent, onLockedFilter }: DomainsFilterRailProps) => {
    const entries = useMemo(() => resolveRailFilters(filterCatalog, query), [filterCatalog, query]);
    // Price, TLD and Status open on arrival, as on Browse, plus any filter the search on screen already narrows.
    const [openIds, setOpenIds] = useState<ReadonlySet<string>>(
        // Browse's own list (the shared catalog, a constants file: Browse's sidebar stays out of `/domains`' chunk).
        () => new Set([...DEFAULT_BROWSE_EXPANDED_FILTER_IDS, ...query.rules.map((rule) => rule.filterId)]),
    );
    const toggle = (filterId: string) =>
        setOpenIds((current) => {
            const next = new Set(current);
            if (next.has(filterId)) next.delete(filterId);
            else next.add(filterId);
            return next;
        });

    const renderBody = (entry: RailFilter) => {
        const { filter } = entry;
        if (filter.control === 'range')
            return <DomainsRailRange label={filter.label} unit={filter.unit} bounds={readRangeBounds(filter)} onIntent={() => onFilterIntent(query)} />;
        if (filter.id === BROWSE_PATTERN_FILTER_ID)
            return (
                <DomainsRailPattern
                    initialOperator={readPatternOperator(entry)}
                    initialValue={typeof entry.rule?.value === 'string' ? entry.rule.value : ''}
                    onApply={(operator, pattern) => onFilterIntent(buildPatternIntent(query, entry, operator, pattern))}
                />
            );
        if (filter.control === 'select' || filter.control === 'multiselect' || filter.control === 'chips')
            return (
                <DomainsRailChoice
                    options={filter.options ?? []}
                    selected={readSelectedValues(entry.rule)}
                    onPick={(value) => onFilterIntent(buildChoiceIntent(query, entry, value))}
                />
            );
        // Every other control (toggle, list, dates) belongs to an advanced filter, which a visitor only meets locked.
        return null;
    };

    return (
        <div class="dm-railstack" data-testid="database-browse-filter-sidebar">
            {entries.map((entry) => (
                <DomainsRailAccordion
                    key={entry.filter.id}
                    filterId={entry.filter.id}
                    label={entry.filter.label}
                    icon={getRadarRuleControlIcon(entry.filter.id)}
                    summary={entry.summary}
                    open={openIds.has(entry.filter.id)}
                    locked={entry.locked}
                    onToggle={() => toggle(entry.filter.id)}
                    onLocked={() => onLockedFilter({ id: entry.filter.id, label: entry.filter.label })}
                >
                    {renderBody(entry)}
                </DomainsRailAccordion>
            ))}
        </div>
    );
};

export default DomainsFilterRail;
